import React, { useCallback, useContext, useMemo, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Portal } from '../../hybrid/wrappers';
import FormComponent from '../form/FormComponent/FormComponent';
import { getFormFields, getFormStyles, sendFunction } from './utils';
import UserContext, { UserProfileContext } from '../../common/utils/getContext';
import SearchWindowHead from './SearchWindowHead';
import { useWindowDimensions } from '../../common/utils/windowDimensions';
import { useWidgetTranslates } from '../../common/utils/utils';
import css from './search-window.module.scss';
import { generateCompStylers } from '../../common/utils/styles';
import classnames from 'classnames';
import UserTable from './components/UserTable';

const SearchWindow = ({
    closeModal,
    sendData,
    title,
    customData,
    gridHandlers,
    gridConfig = {},
    portalClass,
    transformSearchData,
    thead,
    customStyle,
    input = {},
    okHandler = {},
    selectedIds = [],
    fastSearch = false,
    ranks,
    type
}) => {
    const { stylers, classes } = generateCompStylers(customStyle);
    const widthDimensions = useWindowDimensions();
    const screenWidth = useMemo(() => widthDimensions < 768, [widthDimensions]);
    const { userToken, setAlertData, lang, display_country } = useContext(UserContext);
    const { profileData, adminPage } = useContext(UserProfileContext) || {};

    const [fastSearchData, setFastSearchData] = useState({});

    const trans = useWidgetTranslates('searchWindow');

    const [userTableData, setUserTableData] = useState(false);

    const sender = useCallback(
        (val) => {
            sendFunction(
                val,
                sendData,
                setUserTableData,
                userToken,
                setAlertData,
                lang,
                transformSearchData,
                profileData?.data?.data?.id || profileData?.id,
                selectedIds,
                adminPage,
                display_country,
                fastSearch,
                fastSearchData
            );
        },
        [
            sendData,
            userToken,
            setAlertData,
            lang,
            transformSearchData,
            profileData?.data?.data?.id,
            selectedIds,
            adminPage,
            display_country,
            fastSearch,
            fastSearchData
        ]
    );

    useEffect(() => {
        if (fastSearch) return;
        sender('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fastSearch]);

    const interval = useRef(null);
    const searchInputFunc = useCallback(
        (val) => {
            clearInterval(interval.current);
            interval.current = setInterval(() => {
                sender(val);
                clearInterval(interval.current);
            }, 500);
        },
        [interval, sender]
    );

    const formFields = useMemo(() => {
        return getFormFields(trans, fastSearch, input, sender, ranks, type);
    }, [trans, fastSearch, input, sender, ranks, type]);

    // const UserTable = useMemo(() => () => getUserTable(userTableData, screenWidth, gridHandlers, gridConfig, thead, transformSearchData, trans), [userTableData]);

    return (
        <Portal
            unscrollable={customData?.unscrollable}
            closeHandler={closeModal}
            customClass={portalClass}
            fullWidth={screenWidth}
            fullHeight={screenWidth}
        >
            <div
                className={classnames(css.searchWindowWrapper, classes.searchWindowWrapper)}
                style={stylers.searchWindowWrapper}
                //  onClick={(e) => e.stopPropagation()}
            >
                <SearchWindowHead title={title} closeModal={closeModal} customStyle={customStyle} />

                <div className={css.searchWindowTableWrapper}>
                    <FormComponent
                        formStyles={getFormStyles}
                        data={[
                            ...formFields,
                            {
                                type: 'info-field',
                                html: (
                                    <UserTable
                                        userTableData={userTableData}
                                        screenWidth={screenWidth}
                                        gridHandlers={gridHandlers}
                                        gridConfig={gridConfig}
                                        thead={thead}
                                        transformSearchData={transformSearchData}
                                        translates={trans}
                                        fastSearch={fastSearch}
                                    />
                                )
                            }
                        ]}
                        funcs={
                            fastSearch
                                ? {
                                      username: {
                                          onChange: (value) => setFastSearchData((s) => ({ ...s, ['username']: value })),
                                          onKeyDown: (attribute, value, type, errors, event) => {
                                              if (event.key === 'Enter') {
                                                  sender();
                                              }
                                          }
                                      },
                                      name: {
                                          onChange: (value) => setFastSearchData((s) => ({ ...s, ['name']: value })),
                                          onKeyDown: (attribute, value, type, errors, event) => {
                                              if (event.key === 'Enter') {
                                                  sender();
                                              }
                                          }
                                      },
                                      rank_id: {
                                          onChange: (value) => setFastSearchData((s) => ({ ...s, ['rank_id']: value?.id })),
                                          onKeyDown: (attribute, value, type, errors, event) => {
                                              if (event.key === 'Enter') {
                                                  sender();
                                              }
                                          }
                                      }
                                  }
                                : {
                                      search: {
                                          onChange: (value) => searchInputFunc(value)
                                      }
                                  }
                        }
                        sendData={okHandler}
                        // add={[{
                        //     position: 'middle',
                        //     comp: <UserTable
                        //         userTableData={userTableData}
                        //         screenWidth={screenWidth}
                        //         gridHandlers={gridHandlers}
                        //         gridConfig={gridConfig}
                        //         thead={thead}
                        //         transformSearchData={transformSearchData}
                        //         translates={trans}
                        //     />
                        // }]}
                        buttons={[
                            {
                                type: 'ok',
                                buttonText: customData?.buttonText
                            },
                            {
                                type: 'cancel',
                                buttonText: customData?.cancelText,
                                clickFunc: () => closeModal()
                            }
                        ]}
                        buttonNotShow={customData?.buttonNotShow}
                        customData={{
                            fieldsWrapper: css.fieldsWrapper
                        }}
                    />
                </div>
            </div>
        </Portal>
    );
};
SearchWindow.propTypes = {
    styles: PropTypes.object,
    closeModal: PropTypes.func,
    sendData: PropTypes.object,
    title: PropTypes.node,
    customData: PropTypes.object,
    gridHandlers: PropTypes.object,
    gridConfig: PropTypes.object,
    portalClass: PropTypes.string,
    fixedWidth: PropTypes.bool,
    transformSearchData: PropTypes.func,
    thead: PropTypes.object,
    customStyles: PropTypes.object,
    input: PropTypes.object
};
export default SearchWindow;
